'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useTranslation } from 'react-i18next';

// UI lib components
import { AppShell, Group, Avatar, Stack, Text } from '@mantine/core';

// UI local components
import WorkspaceSelector from '@features/Workspace/WorkspaceSelector';
import LevelItems from './LevelItems';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import useAccount from '@features/Account/store/useAccount';

// Local helpers & utils
import { canAccessWorkspace, isWorkspaceAdmin } from '@app/shared/helpers/auth';

// Types
import { NavBarProps, NavBarLevel } from './types';

// Style
import * as classes from './styles/index.css';
import { palette } from '@app/shared/ui-kit/colors';
import NavBarToggleButton from './NavBarToggleButton';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function NavBar({ ...props }: NavBarProps) {
  /* ********************************** HOOKS ********************************* */

  // Store
  const workspace = useWorkspace();
  const { account: user } = useAccount();

  // Localization
  const { t } = useTranslation(['Common', 'WorkspaceLayout', 'Workspace']);

  /* ******************************** CALLBACKS ******************************* */

  /* ***************************** RENDER HELPERS ***************************** */

  /**
   * Renders branding title
   */
  function getTitle() {
    return (
      <Group gap={0} className={classes.title} wrap="nowrap">
        <Text fw={700} c={palette.blackroomWhite} style={{ fontSize: 22 }}>
          {t('Common:brand.nameLeft')}
        </Text>
        <Text fw={200} c={palette.blackroomWhite} style={{ fontSize: 22 }}>
          {t('Common:brand.nameRight')}
        </Text>
      </Group>
    );
  }

  /* ******************************** RENDERING ******************************* */

  if (!user || !workspace) {
    return null;
  }

  return (
    <AppShell.Navbar className={classes.navBar}>
      <Group className={classes.header} wrap="nowrap">
        <Group className={classes.brand} gap={8} wrap="nowrap">
          <Avatar
            className={classes.logo}
            alt="Blackroom"
            src="/images/brand/logo.svg"
          />
          {getTitle()}
        </Group>
        <NavBarToggleButton />
      </Group>

      <WorkspaceSelector />

      {canAccessWorkspace(user, workspace) ||
      isWorkspaceAdmin(user, workspace) ? (
        <Stack className={classes.levelItems} gap="sm" pt={0}>
          <LevelItems level={NavBarLevel.Primary} />
          <LevelItems level={NavBarLevel.Secondary} />
        </Stack>
      ) : null}
    </AppShell.Navbar>
  );
}

export default NavBar;
